import { type ReactNode, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { useKBar } from "kbar";
import { isMacOs } from "react-device-detect";
import { useToasts } from "react-toast-notifications";

import config from "@/environment";
import DatasetDoesNotExist from "@components/error-pages/dataset-does-not-exist";
import { ImpersonateClientSelect } from "@components/shared/selects";
import { LoadingScreen } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { AdminModal } from "@components/v2/modals";
import { useAdminModalIsOpen } from "@components/v2/modals/admin-modal/admin-modal";
import { useCommandPaletteContext } from "@features/command-palette/hooks";
import { useNorthbeamQuery } from "@hooks/http";
import { useCompanyDropdownUsers } from "@hooks/use-company-dropdown-users";
import { logEvent } from "@internal/analytics";
import { type Navbar as NavbarQuery } from "@north-beam/nb-client-side";
import { NAVBAR_QUERY } from "@north-beam/nb-client-side/queries";
import { PrimaryButton } from "@shared/buttons";
import { usePrefs } from "@utils/client-side-preferences";

import { useAppVersion } from "../data/app-version";

import { CollapsibleSidebar } from "./collapsible-sidebar";
import { ChromeProvider } from "./context";
import { Sidebar } from "./dashboard-v2/sidebar";
import { Topbar } from "./dashboard-v2/topbar";
import { HeaderHamburgerMenu } from "./header-hamburger-menu";
import { NavbarDivider } from "./nav-bar-divider";
import { NotificationBellWithMenu } from "./notification-bell-with-menu";
import { TopBarDataFreshnessTooltip } from "./top-bar-data-freshness-tooltip";
import { TopBarLogoAndDynamicTitle } from "./top-bar-logo-and-dynamic-title";

const ReloadPageToast = () => (
  <div>
    Northbeam has been updated.{" "}
    <PrimaryButton
      onClick={() => {
        logEvent("App reload due to server version change");
        window.location.reload();
      }}
    >
      Reload
    </PrimaryButton>
  </div>
);

const parseConnectionIssues = (data: NavbarQuery | undefined) => {
  try {
    return (
      data?.me?.accountMaintenanceItems?.map(
        (item) => JSON.parse(item.id).id,
      ) ?? []
    );
  } catch {
    return [];
  }
};

export const Chrome = ({
  children,
  onboarding,
}: {
  children: ReactNode;
  onboarding?: boolean;
}) => {
  const { data, loading } = useNorthbeamQuery<NavbarQuery>(NAVBAR_QUERY, {
    pollInterval: 60_000, // every 1 mins
    doNotRaiseErrorOnFailure: true,
  });

  const { appVersion } = useAppVersion();
  const { user } = useUser();
  const { addToast } = useToasts();
  const prefs = usePrefs();
  const { query } = useKBar();
  const { unregisterActionCallbacks } = useCommandPaletteContext();

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useAdminModalIsOpen();

  const usersList = useCompanyDropdownUsers(data?.users);

  const [currentAppVersion, setCurrentAppVersion] = useState<string | null>(
    appVersion,
  );

  const [currentServerVersion, setCurrentServerVersion] = useState<
    string | undefined
  >(data?.currentServerVersion);

  const [activePage, setActivePage] = useState<string>("");

  useEffect(() => {
    /* HACK HACK HACK
       This attempts to find the first path argument after the client ID in the URL bar */
    const splitPathName = window.location.pathname.split("/");
    if (splitPathName.length >= 3) {
      setActivePage(splitPathName[2]);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [window.location.pathname]);

  const [_useDevelopmentAnalyticsColors, setUseDevelopmentAnalyticsColors] =
    useState<boolean>(false);

  // Reload page if app or server version changes
  useEffect(() => {
    if (!currentAppVersion) {
      setCurrentAppVersion(appVersion);
    }

    if (!currentServerVersion) {
      setCurrentServerVersion(data?.currentServerVersion);
    }

    if (
      (appVersion && currentAppVersion && currentAppVersion !== appVersion) ||
      (data?.currentServerVersion &&
        currentServerVersion &&
        currentServerVersion !== data.currentServerVersion)
    ) {
      addToast(<ReloadPageToast />, {
        appearance: "info",
        autoDismiss: false,
        id: "reload-app-notification",
      });
      return;
    }
  }, [
    appVersion,
    currentAppVersion,
    setCurrentAppVersion,
    currentServerVersion,
    data?.currentServerVersion,
    addToast,
  ]);

  const brokenConnections = useMemo(() => parseConnectionIssues(data), [data]);

  const numIncidents = data?.me?.activeIncidentCount ?? 0;
  const numMaintenanceItems = data?.me?.activeAccountMaintenanceItemCount ?? 0;
  const datasetExists = !!data?.me?.datasetExists;

  useEffect(() => {
    setUseDevelopmentAnalyticsColors(
      config.isDevelopmentEnvironment || prefs.useDevelopmentAnalyticsData,
    );
  }, [setUseDevelopmentAnalyticsColors, prefs.useDevelopmentAnalyticsData]);

  if (user.featureFlags.enableDashboardV2) {
    if (loading || !data) {
      return <LoadingScreen />;
    }

    if (onboarding) {
      return (
        <div className="flex-1 grid grid-rows-[76px_auto]">
          <div className="row-start-1 row-end-1">
            <Topbar
              isExpanded={isSidebarExpanded}
              onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
              onboarding
            />
          </div>
          <div className="row-start-2 row-end-2 overflow-auto">
            <ChromeProvider isSidebarExpanded={isSidebarExpanded}>
              {children}
            </ChromeProvider>
          </div>
        </div>
      );
    }

    if (
      !(
        datasetExists ||
        activePage === "settings" ||
        onboarding ||
        process.env.REACT_APP_NB_ENV === "dev-local"
      )
    ) {
      return <DatasetDoesNotExist />;
    }

    return (
      <div
        className={clsx("flex-1 grid grid-rows-[76px_auto]", {
          "grid-cols-[73px_auto]": !isSidebarExpanded,
          "grid-cols-[240px_auto]": isSidebarExpanded,
        })}
      >
        <div className="col-start-1 col-end-3 row-start-1 row-end-2">
          <Topbar
            isExpanded={isSidebarExpanded}
            onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
          />
        </div>
        <motion.div
          layout
          transition={{ duration: 0.1 }}
          className="col-start-1 col-end-2 row-start-2 row-end-3"
        >
          <Sidebar
            isExpanded={isSidebarExpanded}
            onShowAdminModal={() => setIsAdminModalOpen(true)}
          />
          <AdminModal
            isOpen={isAdminModalOpen}
            onClose={() => setIsAdminModalOpen(false)}
          />
        </motion.div>
        <motion.div
          layout
          transition={{ duration: 0.1 }}
          className="col-start-2 col-end-3 row-start-2 row-end-3 overflow-auto bg-v2-primary"
        >
          <ChromeProvider isSidebarExpanded={isSidebarExpanded}>
            {children}
          </ChromeProvider>
        </motion.div>
      </div>
    );
  } else {
    if (loading || !data) {
      return <LoadingScreen />;
    }

    return (
      <>
        {!onboarding && (
          <CollapsibleSidebar
            onShowAdminModal={() => setIsAdminModalOpen(true)}
          />
        )}
        <header
          className={clsx(
            "w-full h-14 absolute flex justify-content-between items-center bg-white z-[102]",
            "border-b pr-4",
          )}
        >
          <div className="flex items-center gap-6">
            <TopBarLogoAndDynamicTitle />
            {user.featureFlags.enableCommandPalette && !onboarding && (
              <button
                className={clsx(
                  "text-xs bg-cosmic-4 rounded-md py-[5px] px-2 w-[400px] text-cosmic",
                  "flex items-center justify-between",
                )}
                onClick={() => {
                  unregisterActionCallbacks();
                  query.toggle();
                }}
              >
                <span>
                  <i className="fa-regular fa-sharp fa-magnifying-glass mr-2"></i>
                  Search Northbeam
                </span>
                <span className="flex items-center gap-1">
                  {isMacOs ? (
                    <span
                      className={clsx(
                        "text-xxs border border-cosmic-2 rounded w-[18px] h-[18px]",
                        "flex items-center justify-center",
                      )}
                    >
                      <i className="fa-regular fa-sharp fa-command"></i>
                    </span>
                  ) : (
                    <span
                      className={clsx(
                        "text-xxs border border-cosmic-2 rounded px-1 h-[18px]",
                        "flex items-center justify-center",
                      )}
                    >
                      Ctrl
                    </span>
                  )}
                  <span
                    className={clsx(
                      "text-xxs border border-cosmic-2 rounded w-[18px] h-[18px]",
                      "flex items-center justify-center",
                    )}
                  >
                    K
                  </span>
                </span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!onboarding && (
              <>
                <TopBarDataFreshnessTooltip
                  brokenConnections={brokenConnections}
                />
                <NavbarDivider />
              </>
            )}
            {usersList && usersList.length > 0 && (
              <ImpersonateClientSelect
                inputClassName="w-56"
                user={user}
                data={usersList}
              />
            )}
            {!onboarding && (
              <NotificationBellWithMenu
                numIncidents={numIncidents}
                numMaintenanceItems={numMaintenanceItems}
              />
            )}
            {!onboarding && <HeaderHamburgerMenu />}
          </div>
        </header>
        {datasetExists ||
        activePage === "settings" ||
        onboarding ||
        process.env.REACT_APP_NB_ENV === "dev-local" ? (
          <div
            id="app-scrollable-content-wrapper"
            className={clsx("col overflow-allowed bg-cosmic-4 mt-[56px]", {
              "pl-[56px]": !onboarding,
            })}
          >
            <ChromeProvider isSidebarExpanded={isSidebarExpanded}>
              {children}
            </ChromeProvider>
            <AdminModal
              isOpen={isAdminModalOpen}
              onClose={() => setIsAdminModalOpen(false)}
            />
          </div>
        ) : (
          <DatasetDoesNotExist />
        )}
      </>
    );
  }
};
